import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Reveal } from "../components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState("error");
    };
    poll();
  }, [sessionId]);

  return (
    <section className="pt-[72px] hero-grid-bg min-h-screen flex items-center" data-testid="payment-success-page">
      <div className="max-w-xl mx-auto px-5 sm:px-8 py-24 text-center w-full">
        <Reveal>
          <div className="bg-white rounded-[2rem] border border-ink/5 shadow-[0_24px_64px_rgba(20,22,46,0.1)] p-10 sm:p-14">
            {state === "checking" && (
              <div data-testid="payment-checking">
                <Loader2 className="w-12 h-12 text-indigo animate-spin mx-auto mb-6" />
                <h1 className="font-display font-bold text-2xl text-ink mb-3">Confirming your payment...</h1>
                <p className="text-slate text-sm">This only takes a few seconds.</p>
              </div>
            )}
            {state === "paid" && (
              <div data-testid="payment-confirmed">
                <span className="w-16 h-16 rounded-full bg-mint/15 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-8 h-8 text-mint" />
                </span>
                <h1 className="font-display font-bold text-2xl sm:text-3xl text-ink mb-3">Payment received.</h1>
                <p className="text-slate text-sm leading-relaxed mb-8">
                  Thank you — your payment to Avora Business Solutions has been processed successfully. A receipt is on its way to your inbox from Stripe.
                </p>
                <Link to="/" data-testid="payment-home-link" className="inline-flex items-center justify-center bg-ink text-white font-semibold px-7 py-3 rounded-full hover:bg-indigo transition-colors duration-300 text-sm">
                  Back to avora
                </Link>
              </div>
            )}
            {state === "error" && (
              <div data-testid="payment-error">
                <span className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-8 h-8 text-red-400" />
                </span>
                <h1 className="font-display font-bold text-2xl text-ink mb-3">We couldn't confirm that payment.</h1>
                <p className="text-slate text-sm leading-relaxed mb-8">
                  If money left your account, don't worry — email us at info@avorabusinesssolutions.com.au and we'll sort it out right away.
                </p>
                <Link to="/pay" data-testid="payment-retry-link" className="inline-flex items-center justify-center bg-ink text-white font-semibold px-7 py-3 rounded-full hover:bg-indigo transition-colors duration-300 text-sm">
                  Try again
                </Link>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
